/**
 * Add follow-up — a FULL PAGE at `/countries/:cc/inquiries/:inquiryId/follow-up`
 * (WEB TIER A · WT5).
 *
 * ===========================================================================
 * ⚠ THE DESTINATION IS DERIVED, NOT DRAWN — flagged in the cluster report
 * ===========================================================================
 * No mockup frame draws a follow-up surface. The owner's ruling pair (forms
 * are full pages + the slide-over is killed entirely) forces `FollowupPanel`'s
 * openers somewhere, and a routed page under the record is the conservative
 * landing: same field set, same permissions, same two-write choreography, and
 * cheap to re-route if the owner rules otherwise. The page dress is the house
 * write-surface grammar the approved forms DO draw (numbered cards, ReadyBar,
 * kv aside) — no new vocabulary was invented for it.
 *
 * ===========================================================================
 * CARRIED FROM `FollowupPanel`, VERBATIM IN MEANING
 * ===========================================================================
 * The panel's five recorded decisions all survive:
 *  1. "Call date" is `next_action_at` and is labelled Next action — WHEN the
 *     call happened is `created_at`, which the server sets.
 *  2. The kind selector exists because `kind` is a NOT NULL ENUM the design
 *     drew no control for; `extra_call` keeps the warning treatment the Extra
 *     Call badge uses.
 *  3. OUTCOME is optional and it is a SECOND request — a follow-up cannot move
 *     a stage ("Stage moves go through `/stage` and nowhere else"). The page
 *     says so beside the choice, because the honest failure mode is "the
 *     follow-up was recorded and the stage change was refused".
 *  4. Lost collects its reason here (`LOST_REASON_REQUIRED` is a 422).
 *  5. Nothing is autosaved: the POST is the save, and the ReadyBar states it.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  Alert,
  Button,
  KvList,
  KvRow,
  PageHead,
  ReadyBar,
  RecordCard,
  SkeletonBlock,
  StagePill,
  StepCard,
  TextAreaField,
} from '../../components/ui';
import { BOARD_STAGES, stageTabLabel } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import { cn } from '../../lib/cn';
import { LOST_REASON_MAX } from './LostReasonDialog';
import { createErrorText, peekErrorText } from './errors';
import { formatDateLong, fromDateInputValue, toDateInputValue } from './format';
import { useCreateFollowup, useInquiryRecord, useStageMoveController } from './use-inquiries';
import { useInquiryQuery } from './query-state';
import type { MoveSource } from './stage-move';

type Kind = 'followup' | 'extra_call';

const KIND_OPTIONS: readonly { value: Kind; label: string; hint: string }[] = [
  { value: 'followup', label: 'Follow-up', hint: 'A planned touchpoint.' },
  {
    value: 'extra_call',
    label: 'Extra call',
    hint: 'An unplanned chase — badged on the timeline.',
  },
];

export function FollowupPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const { can } = useSession();
  const { search } = useInquiryQuery();

  const canEdit = can(countryCode, 'inquiries.edit');
  const canStage = can(countryCode, 'inquiries.stage');
  const id = inquiryId ?? null;
  const recordHref = `/countries/${countryCode}/inquiries/${id ?? ''}${search}`;

  if (!canEdit) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Follow-ups are not available to this account">
          Adding a follow-up in {countryCode} needs the Inquiries edit permission.
        </Alert>
        <Link to={recordHref} className="w-fit text-body13 text-indigo hover:underline">
          Back to the record
        </Link>
      </div>
    );
  }

  if (id === null) {
    return (
      <div className="p-6">
        <Alert tone="danger">This URL names no inquiry, so nothing can be added.</Alert>
      </div>
    );
  }

  /* The keyed inner component is how the form and the mutation are emptied
   * between inquiries — the panel's own device, kept: `key={id}` gives every
   * inquiry a fresh instance, so a half-typed note cannot appear over a
   * different customer's record. */
  return (
    <FollowupForm
      key={id}
      countryCode={countryCode}
      inquiryId={id}
      recordHref={recordHref}
      canStage={canStage}
    />
  );
}

function FollowupForm({
  countryCode,
  inquiryId,
  recordHref,
  canStage,
}: {
  countryCode: string;
  inquiryId: string;
  recordHref: string;
  canStage: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const record = useInquiryRecord(inquiryId);
  const inquiry = record.data ?? null;
  const create = useCreateFollowup(countryCode, inquiryId);
  const mover = useStageMoveController(countryCode);

  const [kind, setKind] = useState<Kind>('followup');
  const [note, setNote] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [outcome, setOutcome] = useState<InquiryStage | null>(null);
  const [lostReason, setLostReason] = useState('');

  const needsReason = outcome === 'lost' && lostReason.trim() === '';
  const canSave = inquiry !== null && note.trim() !== '' && !needsReason && !create.isPending;

  const save = (): void => {
    if (inquiry === null || !canSave) return;
    const iso = nextAction === '' ? null : fromDateInputValue(nextAction);
    create.mutate(
      {
        kind,
        note: note.trim(),
        ...(iso === null ? {} : { next_action_at: iso }),
      },
      {
        onSuccess: () => {
          // The stage change is deliberately AFTER the follow-up: if it is refused, the note
          // the operator typed is already safe, and the controller reports the refusal on its
          // own. The reverse order could lose the note to a 422 about a different field.
          if (outcome !== null && outcome !== inquiry.stage) {
            const source: MoveSource = {
              id: inquiry.id,
              doc_no: inquiry.doc_no,
              stage: inquiry.stage,
              lost_reason: inquiry.lost_reason,
            };
            mover.requestMove(source, outcome, outcome === 'lost' ? lostReason.trim() : undefined);
          }
          // The page's "closed" is the record it belongs to; the move's toast
          // (and its Undo) survive the navigation — the provider layout's
          // documented purpose.
          navigate(recordHref);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHead
        title="Add follow-up"
        sub={
          inquiry === null
            ? 'The record is loading.'
            : `${inquiry.party_name ?? 'Unknown customer'} · ${inquiry.doc_no} — the note is written to this record's timeline.`
        }
        actions={
          <>
            <Link
              to={recordHref}
              className="inline-flex h-9 items-center rounded-sm border border-rim px-3 text-body font-medium text-fg hover:bg-subtle"
            >
              Cancel
            </Link>
            <Button onClick={save} disabled={!canSave}>
              {create.isPending ? 'Saving…' : 'Save follow-up'}
            </Button>
          </>
        }
      />

      {record.isPending ? <SkeletonBlock className="h-40 w-full" /> : null}
      {record.isError ? (
        <div className="flex flex-col gap-3">
          <Alert tone="danger">{peekErrorText(record.error)}</Alert>
          <Link to={recordHref} className="w-fit text-body13 text-indigo hover:underline">
            Back to the record
          </Link>
        </div>
      ) : null}
      {create.isError ? <Alert tone="danger">{createErrorText(create.error)}</Alert> : null}

      {inquiry === null ? null : (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <StepCard
              n={1}
              state="now"
              title="The call"
              badge={
                <span className="inline-flex items-center rounded-pill bg-info-bg px-2.5 py-0.5 text-label font-medium text-info-fg">
                  Current
                </span>
              }
            >
              <fieldset className="flex flex-col gap-2">
                <legend className="pb-1.5 text-label font-medium text-fg">Kind</legend>
                <RadioGroup
                  name="followup-kind"
                  options={KIND_OPTIONS.map((option) => ({
                    value: option.value,
                    hint: option.hint,
                    label: (
                      <span
                        className={cn(
                          'inline-flex items-center rounded-pill px-2.5 py-1 text-label font-medium',
                          option.value === 'extra_call'
                            ? 'bg-warn-bg text-warn-fg'
                            : 'bg-subtle text-fg-secondary ring-1 ring-line',
                        )}
                      >
                        {option.label}
                      </span>
                    ),
                  }))}
                  value={kind}
                  onChange={(next) => setKind(next === 'extra_call' ? 'extra_call' : 'followup')}
                />
              </fieldset>

              <TextAreaField
                label="Note"
                rows={4}
                required
                maxLength={20_000}
                value={note}
                placeholder="Add a note about the follow-up call…"
                onChange={(event) => setNote(event.target.value)}
              />

              <div className="flex w-full flex-col gap-1.5">
                <label htmlFor="next-action" className="text-label font-medium text-fg">
                  Next action
                </label>
                <input
                  id="next-action"
                  type="date"
                  value={nextAction}
                  min={toDateInputValue(new Date().toISOString())}
                  onChange={(event) => setNextAction(event.target.value)}
                  className="h-9 w-full rounded-sm border border-rim bg-surface px-3 text-body text-fg outline-none"
                />
                <p className="text-label text-fg-secondary">
                  When this needs chasing again. Leave empty if nothing is scheduled.
                </p>
              </div>
            </StepCard>

            {canStage ? (
              <StepCard
                n={2}
                state="next"
                title="Outcome"
                badge={
                  <span className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-0.5 text-label font-medium text-fg-secondary ring-1 ring-line">
                    Optional
                  </span>
                }
              >
                <RadioGroup
                  name="followup-outcome"
                  options={[
                    { value: '', label: <span className="text-body13 text-fg">No stage change</span> },
                    ...BOARD_STAGES.filter((stage) => stage !== inquiry.stage).map((stage) => ({
                      value: stage,
                      label: <StagePill stage={stage} />,
                    })),
                  ]}
                  value={outcome ?? ''}
                  onChange={(next) =>
                    setOutcome(
                      next === '' ? null : (BOARD_STAGES.find((stage) => stage === next) ?? null),
                    )
                  }
                />
                {outcome === 'lost' ? (
                  <TextAreaField
                    label="Why lost?"
                    rows={2}
                    required
                    maxLength={LOST_REASON_MAX}
                    value={lostReason}
                    placeholder="e.g. Chose a competitor on price"
                    onChange={(event) => setLostReason(event.target.value)}
                    hint={`${String(lostReason.length)}/${String(LOST_REASON_MAX)} — required, and stored on the record.`}
                  />
                ) : null}
                {outcome === null ? null : (
                  <p className="text-label text-fg-secondary">
                    Saving records the follow-up first, then moves the stage. Both are separate
                    entries in the audit trail; if the move is refused the note still stands.
                  </p>
                )}
              </StepCard>
            ) : null}

            <ReadyBar
              statement={
                note.trim() === ''
                  ? 'A note is required before this can be saved.'
                  : needsReason
                    ? 'Lost needs a reason before this can be saved.'
                    : outcome === null
                      ? 'Ready to save. One note is written; no stage moves.'
                      : 'Ready to save. The note is recorded first; the stage move follows it.'
              }
            />
          </div>

          <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
            <RecordCard title="Record">
              <KvList>
                <KvRow label="Customer">{inquiry.party_name ?? '—'}</KvRow>
                <KvRow label="Number" mono>
                  {inquiry.doc_no}
                </KvRow>
                <KvRow label="Stage">{stageTabLabel(inquiry.stage)}</KvRow>
                <KvRow label="In stage since">{formatDateLong(inquiry.stage_entered_at)}</KvRow>
                <KvRow label="Owner">{inquiry.assigned_to_name ?? 'Unassigned'}</KvRow>
              </KvList>
            </RecordCard>

            <RecordCard title="What Save does">
              <p className="text-body13 text-fg-secondary">
                One follow-up row on this inquiry, evented on the timeline. If an outcome is
                chosen, a second, separate request moves the stage — a follow-up cannot move a
                stage by itself, by the server&rsquo;s own design.
              </p>
            </RecordCard>
          </aside>
        </div>
      )}
    </div>
  );
}

/**
 * The radio group the design draws as a column of pills with a leading dot —
 * `FollowupPanel`'s own, moved with the form. A real `<input type="radio">`
 * set: arrow-key navigation, a single tab stop and the group's name announced
 * all come for free, and none of them survive a div-with-onClick.
 */
function RadioGroup({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: readonly { value: string; label: React.ReactNode; hint?: string }[];
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {options.map((option) => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2.5 text-left">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className="size-4 shrink-0 accent-[var(--color-indigo)]"
          />
          {option.label}
          {option.hint === undefined ? null : (
            <span className="text-label text-fg-secondary">{option.hint}</span>
          )}
        </label>
      ))}
    </div>
  );
}
